import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, ArrowLeft, Users } from 'lucide-react';
import { API_URL, CATEGORIES, ARCHIVE_SECTIONS, slugify, deslugify } from '../constants';

const CategoryPage = () => {
  const { categorySlug } = useParams();
  const categoryName = deslugify(categorySlug, CATEGORIES);
  const subcategories = CATEGORIES[categoryName] || [];
  const section = ARCHIVE_SECTIONS.find(s => slugify(s.category) === categorySlug);
  const [peopleCounts, setPeopleCounts] = useState({});

  useEffect(() => {
    // Fetch people counts per subcategory from API
    fetch(`${API_URL}/subcategories/`).then(r => r.json()).then(d => {
      const arr = Array.isArray(d) ? d : (d.results || []);
      const counts = {};
      arr.forEach(sub => {
        const name = sub.name;
        if (sub.people_count !== undefined) counts[name] = sub.people_count;
      });
      setPeopleCounts(counts);
    }).catch(() => {});
  }, []);

  const icons = ['👤','👨‍🎓','⚕️','💼','📻','🏛️','🎓','⚖️','🏗️','⚽'];

  if (!categoryName || subcategories.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500">Category not found.</p>
        <Link to="/archive" className="text-emerald-600 hover:underline mt-4 inline-block">← Back to Archive</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-emerald-600">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/archive" className="hover:text-emerald-600">Archive</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">{categoryName}</span>
      </nav>

      {/* Back button */}
      <Link to="/archive" className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> Back to Archive
      </Link>

      {/* Category header */}
      <div className="bg-emerald-700 text-white rounded-2xl p-8 mb-8">
        <div className="flex items-center gap-4 mb-3">
          <span className="text-5xl">{section?.icon || '📁'}</span>
          <div>
            <h1 className="text-3xl font-bold">{categoryName}</h1>
            <p className="text-emerald-100 mt-1">{section?.description}</p>
          </div>
        </div>
        <p className="text-emerald-200 text-sm">{subcategories.length} subcategories</p>
      </div>

      {/* Subcategories */}
      <h2 className="text-xl font-bold text-gray-900 mb-4">Subcategories</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {subcategories.map((sub, i) => (
          <Link
            key={sub}
            to={`/archive/${categorySlug}/${slugify(sub)}`}
            className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-emerald-400 hover:shadow-lg transition group"
          >
            <div className="flex items-start justify-between mb-3">
              <span className="text-3xl">{icons[i % icons.length]}</span>
              <ChevronRight className="w-5 h-5 text-emerald-600 opacity-0 group-hover:opacity-100 transition" />
            </div>
            <h3 className="font-semibold text-gray-800 group-hover:text-emerald-700 transition leading-tight mb-2">{sub}</h3>
            {peopleCounts[sub] !== undefined && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Users className="w-3 h-3" /> {peopleCounts[sub]} entries
              </p>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default CategoryPage;
